import { getAccessToken } from '@dental-staffing-platform/auth';
import { z } from 'zod';
import type {
  Credential,
  AuditLog,
  DentalGroup,
  Invitation,
  InviteRole,
  Professional,
  Location,
  OfficeShiftListView,
  OfficeCollectionView,
  ShiftStatus,
  HealthStatus,
  IntegrationSummary,
  PaginatedResult,
  PaginationQuery,
  Profession,
  ProfessionalStatus,
  FinanceOverview,
  FinancialSettings,
} from '@dental-staffing-platform/shared';

/**
 * The admin console's door to the dental API. Uses the signed-in admin's token; every endpoint is
 * org-scoped and role-gated server-side (non-admins get 403). Types mirror the API responses.
 */

/** A credential joined with its owner's name — the review-queue row shape. */
export type CredentialReview = Credential & { professionalName: string };
/** Shift shape returned by `/api/shifts/mine` (used for overview counts + pipeline). */
export interface ShiftLite {
  id: string;
  status: ShiftStatus;
  roleRequired: string;
  locationName: string;
  startsAt: string;
  bookingCount: number;
}

type InviteRow = Omit<Invitation, 'token'> & {
  acceptPath?: string;
  expired?: boolean;
  platformAlreadyExists?: boolean;
  message?: string;
};

export type { InviteRow };
export type PageRequest = Partial<PaginationQuery>;

const API_URL: string | undefined = import.meta.env.VITE_API_URL;
if (!API_URL) throw new Error('VITE_API_URL is required');

export class AdminApiError extends Error {
  constructor(
    message: string,
    readonly status: number
  ) {
    super(message);
    this.name = 'AdminApiError';
  }
}

const responseEnvelope = z.object({
  success: z.boolean(),
  data: z.unknown().optional(),
  error: z.string().optional(),
});

export async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const token = getAccessToken();
  const res = await fetch(`${API_URL}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init?.headers ?? {}),
    },
  });
  let raw: unknown;
  try {
    raw = await res.json();
  } catch {
    if (res.status === 401) {
      throw new AdminApiError('Session expired. Sign in again.', 401);
    }
    throw new AdminApiError(`Request failed (${res.status})`, res.ok ? 502 : res.status);
  }
  const parsed = responseEnvelope.safeParse(raw);
  if (!parsed.success) {
    if (res.status === 401) throw new AdminApiError('Session expired. Sign in again.', 401);
    throw new AdminApiError(
      'The server response could not be confirmed. Refresh or retry the same request.',
      res.ok ? 502 : res.status
    );
  }
  const body = parsed.data;
  if (res.status === 401 || /invalid or expired token/i.test(body.error ?? '')) {
    throw new AdminApiError('Session expired. Sign in again.', 401);
  }
  if (!res.ok || !body.success)
    throw new AdminApiError(
      body.error ?? `Request failed (${res.status})`,
      res.ok ? 502 : res.status
    );
  return body.data as T;
}

function queryPath(
  path: string,
  params: Record<string, string | number | boolean | null | undefined> = {}
): string {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') query.set(key, String(value));
  }
  const encoded = query.toString();
  return `${path}${encoded ? `?${encoded}` : ''}`;
}

export const adminApi = {
  finance: {
    overview: (): Promise<FinanceOverview> => req('/api/admin/finance'),
    settings: (): Promise<FinancialSettings> => req('/api/work/settings'),
    updateSettings: (input: FinancialSettings): Promise<FinancialSettings> =>
      req('/api/work/settings', { method: 'PUT', body: JSON.stringify(input) }),
    charge: (earningId: string): Promise<OfficeCollectionView> =>
      req(`/api/admin/finance/collections/${earningId}/charge`, { method: 'POST' }),
    reconcile: (earningId: string): Promise<OfficeCollectionView> =>
      req(`/api/admin/finance/collections/${earningId}/reconcile`, { method: 'POST' }),
  },
  credentials: {
    page: (
      filter: PageRequest & {
        status?: Credential['reviewStatus'];
        type?: Credential['type'];
        professionalId?: string;
        search?: string;
      } = {}
    ): Promise<PaginatedResult<CredentialReview>> => req(queryPath('/api/credentials/org', filter)),
    queue: async (status?: string): Promise<CredentialReview[]> =>
      (
        await req<PaginatedResult<CredentialReview>>(
          queryPath('/api/credentials/org', { status, pageSize: 100 })
        )
      ).items,
    review: (id: string, decision: 'approved' | 'rejected', notes?: string): Promise<Credential> =>
      req(`/api/credentials/${id}/review`, {
        method: 'PATCH',
        body: JSON.stringify(
          notes ? { reviewStatus: decision, reviewNotes: notes } : { reviewStatus: decision }
        ),
      }),
    documentUrl: async (id: string): Promise<string> => {
      const signed = await req<{ url: string; expiresAt: string }>(
        `/api/credentials/${id}/document-link`,
        { method: 'POST' }
      );
      return signed.url;
    },
  },
  professionals: {
    page: (
      filter: PageRequest & {
        status?: ProfessionalStatus;
        profession?: Profession;
        search?: string;
      } = {}
    ): Promise<PaginatedResult<Professional>> => req(queryPath('/api/professionals', filter)),
    list: async (): Promise<Professional[]> =>
      (await req<PaginatedResult<Professional>>('/api/professionals?pageSize=100')).items,
    setStatus: (id: string, status: Professional['status']): Promise<Professional> =>
      req(`/api/professionals/${id}/status`, { method: 'PATCH', body: JSON.stringify({ status }) }),
  },
  invitations: {
    page: (
      filter: PageRequest & {
        status?: Invitation['status'];
        role?: InviteRole;
        search?: string;
      } = {}
    ): Promise<PaginatedResult<InviteRow>> => req(queryPath('/api/invitations', filter)),
    list: async (status?: 'pending'): Promise<InviteRow[]> =>
      (
        await req<PaginatedResult<InviteRow>>(
          queryPath('/api/invitations', { status, pageSize: 100 })
        )
      ).items,
    create: (input: {
      email: string;
      role: InviteRole;
      scopeType?: 'platform' | 'group' | 'location';
      scopeId?: string | null;
      firstName?: string;
      lastName?: string;
    }): Promise<InviteRow> =>
      req('/api/invitations', { method: 'POST', body: JSON.stringify(input) }),
    revoke: (id: string): Promise<InviteRow> =>
      req(`/api/invitations/${id}/revoke`, { method: 'POST' }),
  },
  shifts: {
    page: (
      filter: PageRequest & {
        view?: OfficeShiftListView;
        status?: ShiftStatus;
        role?: Profession;
        locationId?: string;
        from?: string;
        to?: string;
        search?: string;
      } = {}
    ): Promise<PaginatedResult<ShiftLite>> => req(queryPath('/api/shifts/mine', filter)),
    all: async (): Promise<ShiftLite[]> =>
      (await req<PaginatedResult<ShiftLite>>('/api/shifts/mine?pageSize=100')).items,
    cancel: (id: string, reason: string): Promise<ShiftLite | null> =>
      req(`/api/shifts/${id}/cancel`, { method: 'POST', body: JSON.stringify({ reason }) }),
  },
  groups: {
    list: (): Promise<DentalGroup[]> => req('/api/groups'),
    create: (name: string): Promise<DentalGroup> =>
      req('/api/groups', { method: 'POST', body: JSON.stringify({ name }) }),
    update: (id: string, patch: { name?: string; status?: 'active' | 'inactive' }) =>
      req<DentalGroup | null>(`/api/groups/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),
  },
  locations: {
    page: (
      filter: PageRequest & {
        groupId?: string;
        active?: boolean;
        search?: string;
      } = {}
    ): Promise<PaginatedResult<Location>> => req(queryPath('/api/locations', filter)),
    list: async (): Promise<Location[]> =>
      (await req<PaginatedResult<Location>>('/api/locations?pageSize=100')).items,
    update: (id: string, patch: { isActive: boolean }): Promise<Location | null> =>
      req(`/api/locations/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  },
  operations: {
    auditPage: (
      filter: PageRequest & {
        action?: string;
        entityType?: string;
        actorUserId?: string;
        from?: string;
        to?: string;
        search?: string;
      } = {}
    ): Promise<PaginatedResult<AuditLog>> => req(queryPath('/api/admin/audit', filter)),
    audit: async (): Promise<AuditLog[]> =>
      (await req<PaginatedResult<AuditLog>>('/api/admin/audit?pageSize=100')).items,
    exceptions: (): Promise<{
      credentials: Array<Record<string, string | null>>;
      failedNotifications: Array<Record<string, string | null>>;
      cancelledShifts: Array<Record<string, string | null>>;
    }> => req('/api/admin/exceptions'),
  },
  system: {
    health: (): Promise<HealthStatus[]> => req('/api/admin/health'),
    integrations: (): Promise<IntegrationSummary[]> => req('/api/admin/integrations'),
  },
};
